import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as userService from '../services/userService';
import type { CreateUserRequest, UpdateUserRequest } from '../types/api';

// User Management Hooks
export const useUsers = (params?: userService.UsersParams) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => userService.getUsers(params), // OpenAPI returns { users: [...], pagination: {...} }
    select: (data) => {
      // API returns { users: User[], pagination: Pagination }
      // Return consistent structure with users array
      return {
        users: data.users || [],
        pagination: data.pagination || null
      };
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => userService.createUser(data), // OpenAPI returns User directly
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateUserRequest & { id: string }) => 
      userService.updateUser(id, data), // OpenAPI returns User directly
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id), // OpenAPI returns void
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

// Additional hooks for non-OpenAPI endpoints
export const useResetUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; new_password?: string; send_email?: boolean }) =>
      userService.resetUserPassword(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      userService.toggleUserStatus(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useSendWelcomeEmail = () => {
  return useMutation({
    mutationFn: (id: string) => userService.sendWelcomeEmail(id),
  });
};
